import { useEffect, useState } from 'react'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Утилита для объединения классов Tailwind CSS
 * 
 * Объединяет классы с помощью clsx и tailwind-merge для правильной обработки
 * конфликтующих классов Tailwind (например, p-2 и p-4).
 * 
 * @param inputs - Массив классов или объектов с условиями
 * @returns Объединенная строка классов
 * 
 * @example
 * ```ts
 * cn('px-2 py-1', 'bg-red-500') // 'px-2 py-1 bg-red-500'
 * cn('p-2', 'p-4') // 'p-4' (последний перезаписывает предыдущий)
 * cn('px-2', { 'bg-red-500': isActive }) // 'px-2 bg-red-500' если isActive
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Хук для debounce значения
 * 
 * Откладывает обновление значения на указанную задержку.
 * Полезно для оптимизации частых обновлений (например, поиск, фильтры).
 * 
 * @template T - Тип значения
 * @param value - Значение для debounce
 * @param delay - Задержка в миллисекундах (по умолчанию 300ms)
 * @returns Debounced значение
 * 
 * @example
 * ```tsx
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebounce(search, 500)
 * 
 * useEffect(() => {
 *   // Выполнится только через 500ms после последнего изменения search
 *   fetchResults(debouncedSearch)
 * }, [debouncedSearch])
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

